import { css, html, LitElement, nothing, PropertyValues, TemplateResult } from "lit";
import {property, customElement} from 'lit/decorators.js';
import { VoyagePlan } from "../../../src/models/vessel-planning-model";
import { VesselsModel } from "../../../src/models/vessels-model";
import { PlanSource, VoyagePlansModel } from "../../../src/models/voyage-plans-model";
import { GetFormattedDate, GetFormattedDateDayMonthTimeNoYear, mpsToKnots, stringToHTMLAttribute } from "../../../src/utils/helpers";
import { TimeseriesChartTimeFormat, YAxisChartParams } from "../../voy-timeseries-chart/src/echarts-options";
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import '../../voy-timeseries-chart/src/voy-timeseries-chart';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import { speedTooltipText } from "../../../src/utils/utils";
import { AuthService, AuthorizationRoles } from "../../../src/services/auth-service";
import { TimeseriesData } from "../../voy-timeseries-chart/src/echartUtils";
import { AlarmType, AlarmState, AlarmLog } from "../../../src/services/types/fleet-alarms-service.types";
import { FleetAlarm, FleetAlarmLog } from "../../../src/models/types/voy-fleet-alarm-model.types";
import { ScheduleEntry, PortCallActuals } from "../../../src/models/types/voyage-schedules-model.types";
import { VoyageSchedulesModel } from "../../../src/models/voyage-schedules-model";
import { AlarmMarkupTitles, VoyFleetAlarmCustomEvents } from "./types";
import { VoyFleetAlarmModel } from "../../../src/models/voy-fleet-alarm-model";
import { VesselVoyageVersion } from "../../voy-global-filters/src/types";
import { PremiumVesselNotInNextPlatform } from "../../voy-fleet-sub-view/src/types";
import { getFormattedDateTime, getTooltipContent } from "../../acitivity-log/helper";
import { FleetNotificationService } from "../../../src/services/fleet-notification-service";

@customElement("voy-fleet-alarm")
export class VoyFleetAlarm extends LitElement {
    @property({ attribute: false }) private _metadata: Array<{ title: string, value: string }> = [
        { title: AlarmMarkupTitles.ArrETA, value: 'N/A' },
        { title: AlarmMarkupTitles.PlannedSpeed, value: 'N/A' }
    ]
    @property({ attribute: false }) private _plan?: VoyagePlan;
    @property({ attribute: false }) private _schedule?: ScheduleEntry;
    @property({ attribute: false }) private _chartData: TimeseriesData[] = [];
    @property({ attribute: false }) private _chartTimeFormat: TimeseriesChartTimeFormat = TimeseriesChartTimeFormat.Hours;
    @property({ attribute: false }) private _avgPlannedSpeedDiv: HTMLElement|null = null;
    @property({ attribute: false }) private _unit: string = "Sog (kt)";
    @property({ attribute: false }) private _chartStyling?: YAxisChartParams;
    @property({ attribute: false }) private _arrivalPortCallActuals?: PortCallActuals;
    @property({attribute: false}) private _alarmNote: string = "";

    @property({ attribute: false }) public imo?: number; 
    @property({ attribute: false }) public alarm?: FleetAlarm;
    @property({ attribute: false }) public maximized: boolean = false;
    @property({ attribute: false }) public vesselVersion?: string;
    @property({ attribute: false }) public index?: number;

    constructor() {
        super();
        
        this._handleAlarm = this._handleAlarm.bind(this);
        this._getPlan = this._getPlan.bind(this);
        this._updateETA = this._updateETA.bind(this);
        this._alarmStatePill = this._alarmStatePill.bind(this);
        this._updateAvgRequiredSpeed = this._updateAvgRequiredSpeed.bind(this);
        this._getSchedule = this._getSchedule.bind(this);
        this._getPlan = this._getPlan.bind(this);
        this._processDataForChart = this._processDataForChart.bind(this);
        this._getUserName = this._getUserName.bind(this);
        this._getAlarmLabel = this._getAlarmLabel.bind(this);
    }

    static get styles() {
        return [
            voyageCursorStyles,
            css`
                :host {
                    font-family: 'MaerskText';
                    font-size: 12px;
                    line-height: 16px;
                    margin: 0;
                    display: block;
                }

                header {
                    display: flex;
                    flex-direction: row;
                    column-gap: 16px;
                    max-width: 100%;
                    justify-content: space-between;
                    margin-right: 48px;
                }
                header[first-row] {
                    max-width: 84%;
                }

                header h2 {
                    font-size: 16px;
                    margin: 0;
                    font-family: 'Maersk Text';
                    font-style: normal;
                    font-weight: 400;
                    line-height: 24px;
                    padding: 16px;
                    padding-top: 24px;
                    padding-right: 0px;
                    flex: 0 0 auto;
                }

                header[maximized] .pill {
                    margin-left: 16px;
                }

                header .pill {
                    margin-top: 16px;
                    padding: 2px 8px;
                    height: 18px;
                    font-size: 12px;
                    text-align: center;
                    border-radius: 100px;
                }

                header .pill[acknowledged] {
                    background-color: #C5E5C2;;
                    color: #353535;;
                }
                
                header .pill[in-progress] {
                    background-color: #CFCFCF;;
                    color: #353535;
                }

                header .pill[active] {
                    background-color: #B80012;;
                    color: #FFFFFF;
                }

                header .pill[awaiting-reason] {
                    background-color: #B80012;;
                    color: #FFFFFF;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                header .pill[reason-given] {
                    background-color: #FFDE74;
                    color: #141414;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                header[maximized] .pill {
                    padding-bottom: 4px;
                }

                header[maximized]{
                    display: block;
                }

                .graph {
                    background: gray;
                    width: 100%;
                    height: 180px;
                    margin: 0 0 8px 5px;
                    width: 100%;
                }
                .no-chart {
                    padding: 16px 16px 0px;
                }
                .metadata {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: wrap;
                    column-gap: 16px;
                    row-gap: 16px;
                    padding: 16px 16px 0 16px
                }
                .metadata[maximized] > .meta {
                    flex: inherit;
                    max-height: 100px;
                }

                .metadata > .meta {
                    flex: 1 0 47%;
                }
                .metadata > .meta h4,
                .metadata > .meta h5,
                .metadata > .meta p,
                section.note h5,
                section.note p {
                    margin: 0;
                    padding: 0;
                    font-size: 12px;
                    line-height: 16px;
                }

                .metadata > .meta h4 {
                    margin-bottom: 8px;
                    margin-top: -16px;
                }
                .metadata[maximized]  > .meta h4 {
                    margin-top: 0px;
                }

                .metadata > .alarm-data {
                    padding-bottom: 16px;
                }

                section.note {
                    margin-bottom: 16px;
                }

                section.note > .log-note {
                    margin-bottom: 4px;                  
                }

                section.note > .reason {
                    margin-top: 4px;
                }

                .log-change {
                    color: black;
                }
                .alarm-action {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: wrap;
                    column-gap: 16px;
                    font-style: normal;
                    font-weight: 400;
                    font-size: 14px;
                    line-height: 8px;
                }
                .alarm-action p{
                    margin-top: 0px;
                }
                .alarm-action[maximized] {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: wrap;
                    column-gap: 16px;
                    padding-left: 16px;
                    margin-left: auto;
                    font-style: normal;
                    font-weight: 400;
                    font-size: 14px;
                    line-height: 8px;
                }
                .alarm-action[maximized] p {
                    margin-top: 5px;
                }
                #alarm-note {
                    width: 100%;
                    padding: 10px;
                    border: 1px solid #CFCFCF
                }
                .actions {
                    text-align: right;
                    width: 100%
                }
                .actions[maximized] {
                    display: contents;
                    width: 100%
                } 
                .actions[maximized] button { 
                    margin: 38px 0px 24px 0;
                    justify-content: center;
                    align-items: center;
                    padding: 6px 12px;
                    gap: 6px;
                    border-radius: 3px;
                    height: 30px;
                }
                .alarm-action[maximized] button { 
                    margin-top: 100px;
                }
                .alarm-action[maximized] textarea { 
                    height: 19px;
                    margin-top: -22px; 
                    resize: none;              
                 }
                .actions .inprogress-button {
                    background: #FFFFFF;
                    border: 1px solid #CFCFCF;
                    margin: 0px 13px 24px 0px;
                    justify-content: center;
                    align-items: center;
                    padding: 6px 12px;
                    gap: 6px;
                    border-radius: 3px;
                }
                .actions .dismiss-button { 
                    color: #FFFFFF;
                    background: #00243D;
                    margin: 0 0 24px 0;
                    justify-content: center;
                    align-items: center;
                    padding: 6px 12px;
                    gap: 6px;
                    border: none;
                    border-radius: 3px;
                }
                textarea {
                    font-family: MaerskText;
                    resize: vertical;
                }

                .tooltip {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    column-gap: 4px;
                }
                .tooltip svg {
                    width: 16px;
                    height: 16px;
                    column-gap: 4px;
                    fill: var(--neutrals-grey-03, black);
                }

                .capitalize {
                    text-transform: capitalize;
                }

                .alarm-title {
                    margin-top: -8px;
                }

                .alarm-title p{
                    margin-left: 16px;
                    margin-left: 16px;
                    margin-top: -8px;
                    font-size: 16px;
                    font-family: "Maersk Text";
                }

                .alarm-title[maximized]{
                    display: inline-flex;
                }

                .alarm-title[maximized] p{
                    padding-top: 32px;             
                }
                .timestamp {
                    text-transform: none;
                }
                .alarm-details svg{
                    margin-bottom: -2px;
                }
                .view-advice-description{
                    margin-bottom: 16px;
                    font-size: 12px;
                }

            `
        ];
    }
 
    private get _showHandleAlarm(): boolean {
        const alarmState = this.alarm?.alarmState
        let show = AuthService.userHasRole(AuthorizationRoles.AlarmEdit);

        // If show is false at this point, retrun because the user should not
        // have access to the handle alarm button
        if (!show) return show;

        return (
            alarmState === AlarmState.Active || 
            alarmState === AlarmState.InProgress
        )
    }

    async getPdf() {
        const imo = this.alarm?.imo;
        const reason = JSON.parse(this.alarm!.alarmLogs[0]?.reason)
        const planId= reason.plansId || reason.source_plan_id
        const correlationId = reason?.correlation_id
        if(!correlationId) return;
        FleetNotificationService.openWRAdvicePDF(correlationId)
    }

    render() {
        if (!this.alarm) return "";
        // handling the alarm detail view from the alarm log page
        const isActiveAlarm = this.alarm?.alarmState === AlarmState.Active;
        const alarmStartDate = this.alarm ? getFormattedDateTime(this.alarm?.startTs): " ";
        if (!this.vesselVersion && this.imo) {
            this.vesselVersion = VesselsModel!.getVessel(this.imo)?.vesselVersion
        }
        const isHWAlarm = this.alarm.alarmType === AlarmType.ParametricRoll;
        const isPRAlarm = this.alarm.alarmType === AlarmType.HeavyWeather;
        const isWRAlert = this.alarm.alarmType === AlarmType.WRPerformanceAlert || this.alarm.alarmType === AlarmType.WRSafetyAlert || this.alarm.alarmType === AlarmType.WRReject;
        return html`
            <header ?maximized=${this.maximized} ?first-row=${!this.index}>
               <div ?maximized=${this.maximized} class = "alarm-title">
                    <h2 class=${!(isPRAlarm || isHWAlarm) ? "capitalize" : ""}>
                        ${this._getAlarmLabel(this.alarm?.alarmType)}
                        ${!isWRAlert ? html`<span class="timestamp"> expected on <br>${alarmStartDate}</span>`: html`<span><br/>${alarmStartDate}</span>`}
                    </h2>
                    <p>
                    ${!isWRAlert ? html`<span class="alarm-details ${isActiveAlarm? "active" : ""}">
                            Alarm Details 
                            <span>${SvgIcons.infoCircle}
                                <voyage-tooltip-proxy
                                    class="minw-350"
                                    text="${getTooltipContent(this.alarm)}"
                                    html-content
                                    .positionPriority=${["top"]}>
                                </voyage-tooltip-proxy>
                        </span>
                        </span>`: 
                            html`
                            <div class="view-advice-description" >Review Weather Routing Advice, The PDF will open in a new tab: </div>
                            <mc-button fit="small" class="view-advice" variant="outlined" @click = ${() => this.getPdf()}>
                                View advice 
                            <mc-icon slot="trailingicon" icon="square-arrow-up-right" color="black"></mc-icon>
                            </mc-button>`}
                    </p> 
                </div>
                ${!(isHWAlarm || isPRAlarm || isWRAlert) ? this._alarmStatePill(this.alarm?.alarmState) :  nothing}
            </header>
            ${!(isHWAlarm || isPRAlarm || isWRAlert) ? html `
                ${this._chartData.length > 0 
                    ? html`<div class="graph"> 
                                <voy-timeseries-chart .data=${this._chartData} unit=${this._unit} id="other" .chartTimeFormat=${this._chartTimeFormat} ?show-legend=${true} .leftPadding=${30} .yAxisChartParams=${this._chartStyling}></voy-timeseries-chart>
                            </div>`
                    : html `<p class="no-chart">
                    Sorry - we can not show you a graph, as there is no performance data available for this vessel
                    </p>`
                }` : nothing
            }
            ${!(isHWAlarm || isPRAlarm || isWRAlert) ? html `
                <div class="metadata" ?maximized=${this.maximized && !isWRAlert}>
                    ${this._metadata.map(meta => (
                        html`
                            <div class="meta alarm-data">
                                ${
                                    meta.title === AlarmMarkupTitles.PlannedSpeed ? (
                                    html`
                                        <div class="tooltip">
                                            <h5 data-test=${"title-" + stringToHTMLAttribute(meta.title)}>${meta.title}</h5>
                                            ${SvgIcons.lightQuestionCircle}
                                        </div>`
                                    ) :
                                    html`<h5 data-test=${"title-" + stringToHTMLAttribute(meta.title)}>${meta.title + (this._arrivalPortCallActuals?.arrivalAt ? " (actual)" : " (scheduled)")}</h5>`
                                }
                                <p data-test=${"value-" + stringToHTMLAttribute(meta.title)} class="metadata-value">${meta.value}</p>
                            </div>
                        `
                    ))}
                    ${this._showEventLog() ?  html`<div class="meta">
                        <h4 data-test=${"meta-notes"}>Event log</h4>
                        ${this.alarm?.alarmLogs?.map(log => {
                                //exclude the pending state from log
                                if(log.toState !== "pending"){
                                    return html`
                                        <section class="note">
                                            ${this.vesselVersion === VesselVoyageVersion.Basic ? this._renderComment(log) : nothing }
                                            ${this._renderLogState(log)}
                                            ${(this.vesselVersion === VesselVoyageVersion.Premium || this.vesselVersion === VesselVoyageVersion.Cloud)? this._renderVesselComment(log) : nothing }                                      
                                    </section>`
                                }                       
                            }
                        )}
                    </div>`: nothing }
                    ${ 
                    this._showHandleAlarm 
                    ? 
                    html`
                    ${this._renderAlarmFooter()} `: html``}
                    ` : nothing
            }
            <voyage-tooltip position-priority='["top"]' .for=${this._avgPlannedSpeedDiv}>${this._plan ? speedTooltipText(this._plan) : "Plan and planned speed for this vessel is currently not available"}</voyage-tooltip>
            ` 
                
    }

    private _renderAlarmFooter(){
        let footer;
        switch(this.alarm?.alarmState){
            case AlarmState.Active:
                footer = this._getFooterForActiveAlarm();
                break;
            case AlarmState.InProgress:
                footer = this._renderInprogressAlarmFooter();
                break;
            default:
                footer = html``;
        }
        return footer;
    }

    private _renderAlarmOptionalNote(){
        return(
            html`
              <div class="alarm-action" ?maximized=${this.maximized}>
                    <p>Write note (optional)</p>
                    <textarea id="alarm-note" placeholder=${"Write your notes"} .value=${this._alarmNote} @input=${(e: InputEvent) => this._onNoteChange(e)} >${this._alarmNote}</textarea>                
                </div>`
        )
    }

    private _renderActiveAlarmFooter(){
        return (
            html`
            ${this._renderAlarmOptionalNote()}
             <div class="actions" ?maximized=${this.maximized}>
                <button class="inprogress-button" type="button" @click=${this._inProgress}>In Progress</button>
                <button class="dismiss-button" type="button" @click=${this._handleAlarm}>Dismiss</button>
            </div>               
            `
        )
    }

    private _renderInprogressAlarmFooter(){
        return (
            html`
            ${this._renderAlarmOptionalNote()}
             <div class="actions"  ?maximized=${this.maximized}>
                <button class="dismiss-button" type="button" @click=${this._handleAlarm}>Dismiss</button>
            </div>               
             `
        )
    }

    private _renderVesselComment(log: AlarmLog){
        if(log.reasonSource?.toLowerCase() === "vessel" && !!log.reason) {
            return html`
                <p class="log-note reason">Reason ${GetFormattedDate(log.createdAt)} UTC: ${log.reason} </p>
                ${log.note ? html`<p class="log-note">Comment: ${log.note}</p>` : nothing }
                <p class="log-change">By Vessel</p>
            `
       } 
        else return nothing;
    }

    private _renderComment(log: AlarmLog) {
        if(log.toState === AlarmState.Acknowledged) return html` <p class="capitalize log-note">Reason: ${log.reason}</p>`;
        else return nothing;
    }

    private _renderLogState(log: AlarmLog){
        if(log.toState !== AlarmState.Active){
            return html`
            <p class="log-change">
                <span class="capitalize">${log.toState === AlarmState.Acknowledged ? "Dismissed": log.toState}</span> on ${GetFormattedDate(log.createdAt)} UTC
            </p>
            <p class="log-change">By <span class="capitalize">${this._getUserName(log)}</span></p>`
        }
        else return nothing
    }

    private _getFooterForActiveAlarm(){
        if(this.vesselVersion === VesselVoyageVersion.Premium || this.vesselVersion === VesselVoyageVersion.Cloud ){
            return this._getReasonSubmittedStatus() ? this._renderActiveAlarmFooter() : html ``;
         }
        else return this._renderActiveAlarmFooter();
    }

    private async _inProgress() {
        if (!this.alarm) return null;
            await VoyFleetAlarmModel!.updateAlarm(this.alarm?.id!,AlarmState.InProgress ,"", this._alarmNote);
        this._alarmNote = "";
    }

    private async _handleAlarm(): Promise<void> {
    if (!this.alarm) {
        console.error("Cannot handle alarm, because alarm is undefined.")
    return
    }
        // CLF feature enabled for premium vessels now -excluded the vessels without next platform
        if (this.vesselVersion === VesselVoyageVersion.Basic || PremiumVesselNotInNextPlatform?.find(imo => imo === this.alarm?.imo)) {

            const detail = { alarmId: this.alarm.id, alarmType: this.alarm.alarmType, note: this._alarmNote, vesselVersion: this.vesselVersion}
            this.dispatchEvent(new CustomEvent(VoyFleetAlarmCustomEvents.HandleAlarm, { detail }))
            this._alarmNote = "";
        }
        // Reason submission popup is not required for premium vessels-  as the reason needs to be submitted by vessel
        else {
            const alarmUpdated = await VoyFleetAlarmModel!.updateAlarm(this.alarm!.id, AlarmState.Acknowledged, "",this._alarmNote);
            this._alarmNote = "";
            if (!alarmUpdated) return;

            this.alarm = alarmUpdated;
            this.requestUpdate();
        }     
    }

    private _getReasonSubmittedStatus() {
        // check the reasonSourec field from log to see if the vessel submitted a reason for alarm
        let reasonFromVessel:FleetAlarmLog[] = []
        if(this.alarm && this.alarm.alarmLogs){
            reasonFromVessel = this.alarm.alarmLogs.filter( log => log.reasonSource?.toLowerCase() === "vessel" );
        }
        // CLF feature enabled for premium vessels now -excluded the vessels without next platform
        if(reasonFromVessel.length || PremiumVesselNotInNextPlatform?.find(imo => imo === this.alarm?.imo) ) return true;

       return false;
    }

    private _showEventLog(){
        switch(this.alarm?.alarmState) {
            case AlarmState.Active:
                if(this.vesselVersion === VesselVoyageVersion.Basic || PremiumVesselNotInNextPlatform?.find(imo => imo === this.alarm?.imo))  return false
                else {
                    if(!this._getReasonSubmittedStatus()) return false
                }
            default: 
            return true;
        }
    }

    private async _loadData() {
        if (!this.imo) return;
        this._getPlan();
        this._getSchedule();
    }

    private _getUserName(log: FleetAlarmLog) {
        let name = "Unknown User"

        if (!this.alarm) {
            return name
        }

        if (log.userId.toLocaleLowerCase() === "system") {
            return "System"
        }

        if (!log.userData) {
            return name
        }

        return log.userData.displayName
    }

    connectedCallback() {
        super.connectedCallback();
        VoyagePlansModel!.addEventListener("plans-updated", this._getPlan);
        VesselsModel!.addEventListener('vessels-updated', this._getSchedule);
        this._loadData();
    }

    firstUpdated() {
        this._avgPlannedSpeedDiv = this.shadowRoot!.querySelector(".tooltip");
        this._processDataForChart()
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        VoyagePlansModel!.removeEventListener("plans-updated", this._getPlan);
        VesselsModel!.removeEventListener('vessels-updated', this._getSchedule);
    }

    updated(changedProperties: PropertyValues) {
        super.updated(changedProperties);
        if (changedProperties.has('_plan')) {
            this._updateAvgRequiredSpeed();
        }
        if (changedProperties.has("_schedule")) {
            this._updateETA();
            this._updateAvgRequiredSpeed();
        }
        if (changedProperties.has("alarm")) {
            this._processDataForChart();
        }
    }

    private async _getSchedule() {
        if(!this.imo) return;
        const schedules = await VoyageSchedulesModel?.getScheduleFor(this.imo);
        const schedule = schedules?.find(schedule => schedule.active);

        if (!schedule) return;
        
        this._schedule = schedule;
        this._arrivalPortCallActuals = schedule.arrivalPortCall?.GSIS?.actuals;
    }

    private _updateETA() {
        if (!this._schedule) return;

        this._metadata = this._metadata.map((element) => {
            if (element.title === AlarmMarkupTitles.ArrETA) {
                element.value = this._schedule ? GetFormattedDate(this._schedule.arrivalAt) + " UTC" : "N/A";
            }
            return element;
        })
    }

    private async _updateAvgRequiredSpeed() {
        if (!this._plan) return;

        let avgPlannedSpeed: number | null;

        if (this._plan.source === PlanSource.PASSAGE_PLAN) {
            avgPlannedSpeed = this._plan.metadata?.speed || null;
        } else {
            avgPlannedSpeed = this._plan.passagePlan.calculatedValues.routeAverageSpeed;
        }

        // set avgSpeedRequired on this._metaData
        this._metadata = this._metadata.map((element) => {
            if (element.title === AlarmMarkupTitles.PlannedSpeed) {
                element.value = avgPlannedSpeed ? `${avgPlannedSpeed.toFixed(1)} kt` : "--";
            }
            return element;
        })
    }

    private _getPlan() {
        if (!this.imo)  return 
        
        const plans = VoyagePlansModel!.getPlans(this.imo);
        let currentPlan; 
        if (plans?.has(PlanSource.VOYAGE_PLANNING)) {
            currentPlan = plans.get(PlanSource.VOYAGE_PLANNING);
        } else if (plans?.has(PlanSource.ECOVOYAGE)) {
            currentPlan = plans.get(PlanSource.ECOVOYAGE);
        } else if (plans?.has(PlanSource.EXCEL_PLAN)) {
            currentPlan = plans.get(PlanSource.EXCEL_PLAN);
        } else {
            currentPlan = plans?.get(PlanSource.PASSAGE_PLAN);
        }
        this._plan = currentPlan;
    }

    private _alarmStatePill(status?: AlarmState): TemplateResult {
        switch (status) {
            case AlarmState.Active: 
            if(this._isAwaitingReason()){
                  let pillButton = this._getReasonSubmittedStatus() ? html` <span class="pill" reason-given>Reason given</span>` :  html` <span class="pill" awaiting-reason>Awaiting reason</span>`;
                  return pillButton;
                }
                else return html` <span class="pill" active>Active</span>`
                
            case AlarmState.InProgress: 
                return html`
                <span class="pill" in-progress>In progress</span>
                `
            case AlarmState.Acknowledged: 
                return html `<span class="pill" acknowledged>Dismissed</span>`
            default: 
                return html ``
            
            }    
    }

    private _isAwaitingReason(){
        // CLF feature enabled for premium vessels now -excluded the vessels without next platform
        //Awaiting reason pill should be shown only for stoppage, speed and crosstrack alarm
        if(this.vesselVersion === VesselVoyageVersion.Premium || this.vesselVersion === VesselVoyageVersion.Cloud) {
            if(this.alarm?.alarmType !== AlarmType.NoPlan && this.alarm?.alarmType !== AlarmType.HeavyWeather  && this.alarm?.alarmType !== AlarmType.ParametricRoll){
                if(!PremiumVesselNotInNextPlatform?.find(imo => imo === this.alarm?.imo)){
                    return true
                }
            }
        } 
        return false
    }


    private _processDataForChart() {
        if (!this.alarm) return null;

        // calculate if we should use timestamps in hours or in days
        const diffTime = Math.abs(new Date(this.alarm.startTs).getTime() - new Date(this.alarm.endTs).getTime());

        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 1) {
            this._chartTimeFormat = TimeseriesChartTimeFormat.DayAndMonth;
        }

        const sortedDetections = this.alarm?.alarmDetections?.sort((a,b) => {
            return a.createdAt.localeCompare(b.createdAt)
        })
       
        const data:TimeseriesData[] = [];

        if (sortedDetections?.length > 0) {
            const time_st = sortedDetections.map(ad => {
                return new Date(ad.createdAt).getTime();
            });

            if (this.alarm.alarmType === AlarmType.Crosstrack) {
                this._unit = "Distance (NM)"
    
                const offTrackDistanceValues = sortedDetections.map(a => Number(a.metadata?.offTrackDistance?.toFixed(1)));
                const chartData = {
                    name: 'Actual',
                    fillArea: true,
                    color: '#B80012',
                    animate: true,
                    timeseries: {
                        values: offTrackDistanceValues,
                        timestamps: time_st,
                    }
                }
                this._chartStyling = {
                    nameRotate: 0,
                    nameLocation: 'end',
                    padding: [0, 0, 0, 30],
                    nameGap: 9,
                }
                data.push(chartData)

            } else if (this.alarm.alarmType === AlarmType.Stoppage || this.alarm.alarmType === AlarmType.Speed) {
                // all other alarm types should show actual and simulated speed data
                const actualValues = sortedDetections.map(a => Number(mpsToKnots(a.metadata.speed!).toFixed(1)));

                const actualData = {
                    name: 'Actual',
                    fillArea: true,
                    color: '#B80012',
                    animate: true,
                    timeseries: {
                        values: actualValues,
                        timestamps: time_st,
                    }
                }

                data.push(actualData)
                      
                // stoppage and speed alarms got different type of simulated speed
                switch (this.alarm.alarmType) {
                    case AlarmType.Stoppage:
                        const simulatedValues = new Array(sortedDetections.length).fill(6) // stoppage happens at 6 knots

                        const stoppageSimulatedData: TimeseriesData = {
                            name: 'Simulated',
                            fillArea: true,
                            color: '#0073AB4D',
                            animate: true,
                            timeseries: {
                                values: simulatedValues,
                                timestamps: time_st,
                            }
                        }

                        data.push(stoppageSimulatedData)
                        break;
                    case AlarmType.Speed:
                        const simulatedValue = sortedDetections.map(a => Number(mpsToKnots(a.metadata.requiredSpeed!).toFixed(1)));

                        const speedSimulatedData: TimeseriesData = {
                            name: 'Simulated',
                            fillArea: true,
                            color: '#0073AB4D',
                            animate: true,
                            timeseries: {
                                values: simulatedValue,
                                timestamps: time_st,
                            }
                        }

                        data.push(speedSimulatedData);
                        break;
                    }
                
                this._chartStyling = {
                    nameRotate: 0,
                    nameLocation: 'end',
                    padding: [0, 10, 0, 0],
                    nameGap: 9,
                }
            }
        }
        
        this._removeDuplicates(data);
        this._chartData = data
    }

    private _removeDuplicates(data: TimeseriesData[]) {
        for (const dataEntry of data) {
            const existingEntries: Set<number> = new Set<number>()
            const timestamps = dataEntry.timeseries.timestamps;

            for (let i = 0; i < dataEntry.timeseries.timestamps.length; i++) {
                const timestamp = timestamps[i];

                if (existingEntries.has(timestamp)) {
                    dataEntry.timeseries.values.splice(i, 1);
                    dataEntry.timeseries.timestamps.splice(i, 1);

                    if (dataEntry.timeseries.lower) {
                        dataEntry.timeseries.lower.splice(i, 1);
                    }
                    if (dataEntry.timeseries.upper) {
                        dataEntry.timeseries.upper.splice(i, 1);
                    }

                }

                existingEntries.add(timestamp);
            }
        }
    }

    private _onNoteChange(evt: Event) {
        this._alarmNote = (<HTMLInputElement>evt.target).value;
    }

    private _getAlarmLabel(label: string): string{
        if(label === AlarmType.HeavyWeather){
            return "Heavy weather";
        }
        if(label === AlarmType.ParametricRoll){
            return "Parametric roll";
        }
        if(label === AlarmType.WRPerformanceAlert || label === AlarmType.WRSafetyAlert || label === AlarmType.WRReject){
            return "Weather Routing Advice";
        }
        return label;
    }
}
